import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  People as PeopleIcon,
  TrackChanges as GoalsIcon,
  Assignment as TasksIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { apiUrl } from '../../config/api';

interface TeamAnalysis {
  team_size: number;
  total_goals: number;
  total_active_tasks: number;
  goal_distribution: {
    professional: number;
    personal: number;
  };
  task_distribution: {
    high_urgency: number;
    high_importance: number;
    low_growth_value: number;
  };
  members: Array<{
    id: number;
    user_name: string;
    role: string;
    goals_count: number;
    tasks_count: number;
    avg_task_growth_value: number;
  }>;
}

interface WorkloadRecommendation {
  type: string;
  member: string;
  message: string;
}

const Dashboard: React.FC = () => {
  const { user, currentTeam } = useAuth();
  const [teamAnalysis, setTeamAnalysis] = useState<TeamAnalysis | null>(null);
  const [recommendations, setRecommendations] = useState<WorkloadRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      if (!user || !currentTeam) {
        setError('Please select a team');
        setTeamAnalysis(null);
        setRecommendations([]);
        return;
      }

      const [analysisResponse, workloadResponse] = await Promise.all([
        axios.get(apiUrl('/api/analysis/team-overview')),
        axios.get(apiUrl('/api/analysis/workload-balance'))
      ]);

      setTeamAnalysis(analysisResponse.data);
      setRecommendations(workloadResponse.data.recommendations || []);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [user, currentTeam]);

  useEffect(() => {
    if (currentTeam) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, currentTeam]);

  if (!currentTeam) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">
          Please select a team to view the dashboard.
        </Alert>
      </Container>
    );
  }

  const isAdmin = currentTeam.role === 'admin' || currentTeam.role === 'manager';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Strategic Alignment Dashboard
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="subtitle1" color="text.secondary">
          Overview of your team's goals, tasks, and alignment opportunities
        </Typography>
        {isAdmin ? (
          <Chip 
            label="Admin View - Full Team Analytics" 
            color="primary" 
            size="small"
          />
        ) : (
          <Chip 
            label="Personal View - Limited Analytics" 
            color="default" 
            size="small"
          />
        )}
      </Box>

      {/* Key Metrics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PeopleIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Team Size</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {teamAnalysis?.team_size || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active members
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GoalsIcon color="secondary" sx={{ mr: 1 }} />
              <Typography variant="h6">Total Goals</Typography>
            </Box>
            <Typography variant="h4" color="secondary">
              {teamAnalysis?.total_goals || 0}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip 
                label={`${teamAnalysis?.goal_distribution.professional || 0} Prof`} 
                size="small" 
                color="primary"
              />
              <Chip 
                label={`${teamAnalysis?.goal_distribution.personal || 0} Pers`} 
                size="small" 
                color="secondary"
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TasksIcon sx={{ mr: 1, color: 'success.main' }} />
              <Typography variant="h6">Active Tasks</Typography>
            </Box>
            <Typography variant="h4" sx={{ color: 'success.main' }}>
              {teamAnalysis?.total_active_tasks || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {teamAnalysis?.task_distribution.high_urgency || 0} high urgency
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">Optimization</Typography>
            </Box>
            <Typography variant="h4" sx={{ color: 'warning.main' }}>
              {teamAnalysis?.task_distribution.low_growth_value || 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Low-value tasks to review
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Team Members Overview */}
        <Card sx={{ flex: '2 1 500px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team Members Overview
            </Typography>
            <List>
              {teamAnalysis?.members.map((member) => (
                <ListItem key={member.id} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {member.user_name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`${member.goals_count} goals`} size="small" color="primary" />
                          <Chip label={`${member.tasks_count} tasks`} size="small" color="secondary" />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {member.role} • Avg Task Growth Value: {member.avg_task_growth_value.toFixed(1)}/5
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Recommendations */}
        <Card sx={{ flex: '1 1 300px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recommendations
            </Typography>
            {recommendations.length > 0 ? (
              <List>
                {recommendations.map((rec, index) => (
                  <ListItem key={index} divider sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Chip 
                          label={rec.type.replace('_', ' ')} 
                          size="small" 
                          color={
                            rec.type === 'overload' ? 'error' :
                            rec.type === 'low_growth' ? 'warning' : 'info'
                          }
                          sx={{ mb: 1 }}
                        />
                      }
                      secondary={
                        <Typography variant="body2">
                          {rec.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No specific recommendations at this time. Your team workload appears well balanced!
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Dashboard;